import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Form, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import formService from '../services/formService';
import authorizationService from '../services/authorizationService';
import authService from '../services/authService';
import api from '../services/api';
import Select from 'react-select';

const NewInspectionForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [formTypes, setFormTypes] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [authorizedEquipmentIds, setAuthorizedEquipmentIds] = useState([]);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState(null);
  const [error, setError] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingInspectors, setLoadingInspectors] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [showUnauthorizedWarning, setShowUnauthorizedWarning] = useState(false);

  // Helper function to clean equipment name for display
  const getCleanEquipmentName = (equipmentName) => {
    if (!equipmentName) return '';
    // Remove " - ENAS" and " - ADNOC" suffixes
    return equipmentName.replace(' - ENAS', '').replace(' - ADNOC', '');
  };

  useEffect(() => {
    fetchEmployeeIdAndFormTypes();
  }, []);

  const fetchEmployeeIdAndFormTypes = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch form types and all employees/inspectors
      const [formTypesResponse, employeesResponse] = await Promise.all([
        formService.getFormTypes(),
        authService.getProfile() // We'll use this to get all employees later
      ]);

      // if (formTypesResponse.success) {
      //   setFormTypes(formTypesResponse.form_types);
      // }

      if (formTypesResponse.success) {
        // Temporarily hidden: General and Report — not required yet, may be re-enabled later
        const visibleFormTypes = formTypesResponse.form_types.filter(
          (ft) => !['General', 'Report'].includes(ft.type_name)
        );
        setFormTypes(visibleFormTypes);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load data');
      setLoading(false);
    }
  };


  const handleFormTypeSelect = async (formType) => {
    setSelectedFormType(formType);
    setSelectedInspector(null); // Clear inspector selection
    setSelectedEquipmentType(null); // Clear equipment selection
    setInspectors([]);
    setEquipmentTypes([]);
    setAuthorizedEquipmentIds([]);
    setError('');
    setShowUnauthorizedWarning(false);
    setLoadingInspectors(true);

    try {
      // Fetch all employees/inspectors who have ANY authorization for this form type
      const response = await api.get(`/api/forms/inspectors-by-formtype/${formType.form_type_id}`);

      if (response.data && response.data.inspectors) {
        setInspectors(response.data.inspectors);
      }

      setLoadingInspectors(false);
    } catch (err) {
      // If endpoint doesn't exist, we'll create it, but for now fallback
      // Fetch all company users as inspectors
      try {
        const profileResponse = await authService.getProfile();
        // For now, we'll need to create an endpoint to get all inspectors
        setInspectors([]);
        setError('Please contact administrator to set up inspector list');
      } catch (err2) {
        setError(err.message || 'Failed to load inspectors');
      }
      setLoadingInspectors(false);
    }
  };

  const handleInspectorSelect = async (inspector) => {
    setSelectedInspector(inspector);
    setSelectedEquipmentType(null); // Clear equipment selection
    setError('');
    setShowUnauthorizedWarning(false);
    setLoadingEquipment(true);

    try {
      // Fetch equipment types and authorizations for the SELECTED INSPECTOR
      const [equipmentResponse, authorizationResponse] = await Promise.all([
        formService.getEquipmentTypes(selectedFormType.form_type_id),
        authorizationService.getAuthorizedEquipment(inspector.inspector_id, selectedFormType.form_type_id)
      ]);

      if (equipmentResponse.success) {
        setEquipmentTypes(equipmentResponse.equipment_types);
        setAuthorizedEquipmentIds(authorizationResponse.equipment_ids || []);

        // Check if selected inspector has any authorizations for this form type
        if (authorizationResponse.equipment_ids.length === 0) {
          setShowUnauthorizedWarning(true);
        }
      }

      setLoadingEquipment(false);
    } catch (err) {
      setError(err.message || 'Failed to load equipment types');
      setLoadingEquipment(false);
    }
  };

  const handleEquipmentTypeSelect = (equipmentType) => {
    // Allow selection of any equipment type (authorization and template availability checked on Continue)
    setSelectedEquipmentType(equipmentType);
    setError('');
  };

  const isEquipmentAuthorized = (equipmentTypeId) => {
    return authorizedEquipmentIds.includes(equipmentTypeId);
  };

  const handleContinue = async () => {
    if (!selectedFormType || !selectedInspector || !selectedEquipmentType) {
      setError('Please select form type, inspector, and equipment type');
      return;
    }

    // Check if user is authorized for this equipment
    const isAuthorized = authorizedEquipmentIds.includes(selectedEquipmentType.equipment_type_id);

    if (!isAuthorized) {
      setError(`⚠️ Authorization Required: ${selectedInspector.full_name} is not authorized to inspect this equipment type. Please contact your administrator to request authorization.`);
      return;
    }

    try {
      setLoadingTemplate(true);
      setError('');

      // Check if template exists and is active for this combination
      const response = await formService.getTemplate(
        selectedFormType.form_type_id,
        selectedEquipmentType.equipment_type_id
      );

      if (response.success && response.template) {
        // Navigate to dynamic form with template ID and selected inspector
        navigate('/inspection-form/fill', {
          state: {
            templateId: response.template.template_id,
            formType: selectedFormType,
            equipmentType: selectedEquipmentType,
            selectedInspector: selectedInspector  // Pass selected inspector
          }
        });
      } else {
        setError('No template found for this combination. Please contact administrator.');
      }

      setLoadingTemplate(false);
    } catch (err) {
      // Check if this is a template not available error
      if (err.response?.data?.template_not_available) {
        setError(`📋 Template Not Available: The inspection form template for "${selectedEquipmentType.equipment_name}" is not yet available. Please contact your administrator.`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load template');
      }
      setLoadingTemplate(false);
    }
  };

  const getFormTypeIcon = (typeName) => {
    if (!typeName) return 'fa-file'; // Handle undefined/null
    if (typeName.includes('ADNOC')) return 'fa-building';
    if (typeName.includes('ENAS')) return 'fa-certificate';
    if (typeName.includes('General')) return 'fa-clipboard-list';
    if (typeName.includes('Report')) return 'fa-file-alt';
    return 'fa-file';
  };

  const getEquipmentIcon = (equipmentName) => {
    if (!equipmentName) return 'fa-tools'; // Handle undefined/null
    if (equipmentName.includes('Crane')) return 'fa-truck-moving';
    if (equipmentName.includes('Sling')) return 'fa-link';
    if (equipmentName.includes('Chain')) return 'fa-chain';
    if (equipmentName.includes('Hook')) return 'fa-anchor';
    if (equipmentName.includes('Shackle')) return 'fa-grip-horizontal';
    return 'fa-tools';
  };

  // react-select custom styles for equipment type dropdown
  const equipmentSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#198754' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(25, 135, 84, 0.25)' : 'none',
      '&:hover': { borderColor: '#198754' },
      minHeight: '45px'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#198754'
        : state.isFocused
          ? '#d1e7dd'
          : state.isDisabled
            ? '#f8f9fa'
            : 'white',
      color: state.isSelected
        ? 'white'
        : state.isDisabled
          ? '#6c757d'
          : '#212529',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      padding: '6px 10px',
      '&:active': {
        backgroundColor: state.isDisabled ? '#f8f9fa' : '#198754'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      border: '1px solid #dee2e6'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    }),
    singleValue: (base) => ({
      ...base,
      overflow: 'visible'
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '300px'
    })
  };

  // Custom option renderer for equipment type dropdown
  const formatEquipmentOption = (option, { context }) => {
    const { equipmentType, index, isAuthorized } = option;

    if (!equipmentType) return option.label;

    // Compact display for selected value
    if (context === 'value') {
      return (
        <div className="d-flex align-items-center">
          <i className={`fas ${getEquipmentIcon(getCleanEquipmentName(equipmentType.equipment_name))} me-2 text-success`}></i>
          <span className="fw-bold">{getCleanEquipmentName(equipmentType.equipment_name)}</span>
          <Badge bg="success" className="ms-2" style={{ fontSize: '0.65rem' }}>
            Authorized
          </Badge>
        </div>
      );
    }

    // Full detail for dropdown menu
    return (
      <div className="d-flex align-items-center py-1" style={{ opacity: isAuthorized ? 1 : 0.5 }}>
        <div
          className={`me-2 text-center ${isAuthorized ? 'text-secondary' : 'text-muted'}`}
          style={{ width: '30px', flexShrink: 0 }}
        >
          <i className={`fas ${getEquipmentIcon(getCleanEquipmentName(equipmentType.equipment_name))} fa-lg`}></i>
        </div>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center flex-wrap">
            <span className="badge bg-secondary me-1" style={{ fontSize: '0.6rem' }}>
              {index + 1}
            </span>
            <span className="fw-bold text-truncate" style={{ fontSize: '0.85rem' }}>
              {getCleanEquipmentName(equipmentType.equipment_name)}
            </span>
            {isAuthorized ? (
              <Badge bg="success" className="ms-1" style={{ fontSize: '0.6rem' }}>
                <i className="fas fa-check me-1"></i>Authorized
              </Badge>
            ) : (
              <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                <i className="fas fa-lock me-1"></i>Not Authorized
              </Badge>
            )}
          </div>
          {equipmentType.description && (
            <small className="text-muted d-block text-truncate" style={{ fontSize: '0.7rem' }}>
              {equipmentType.description}
            </small>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center" style={{ paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading form types and equipment types...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container fluid className="mt-4 mb-5" style={{ paddingTop: '40px' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold text-primary">
                  <i className="fas fa-plus-circle me-2"></i>
                  New Inspection Form
                </h2>
                <p className="text-muted">
                  Select form type and equipment type to begin inspection
                </p>
              </div>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/dashboard/${user?.role}`)}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Dashboard
              </Button>
            </div>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Selection Area */}
        <Row className="mb-4">
          {/* Form Type Selection */}
          <Col md={4} className="mb-3">
            <Card className="shadow h-100">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-file-alt me-2"></i>
                  Select Form Type
                </h5>
              </Card.Header>
              <Card.Body>
                {formTypes.length === 0 ? (
                  <Alert variant="warning">
                    No form types available. Please contact administrator.
                  </Alert>
                ) : (
                  <div className="d-grid gap-3">
                    {formTypes.map((formType) => (
                      <Card
                        key={formType.form_type_id}
                        className={`cursor-pointer ${selectedFormType?.form_type_id === formType.form_type_id
                            ? 'border-primary border-3'
                            : ''
                          }`}
                        onClick={() => handleFormTypeSelect(formType)}
                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                      >
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className={`me-3 text-center ${selectedFormType?.form_type_id === formType.form_type_id
                                ? 'text-primary'
                                : 'text-secondary'
                              }`}
                            style={{ width: '40px' }}
                          >
                            <i
                              className={`fas ${getFormTypeIcon(formType.type_name)} fa-2x`}
                            ></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 fw-bold">{formType.type_name}</h6>
                            {formType.description && (
                              <small className="text-muted">{formType.description}</small>
                            )}
                          </div>
                          {selectedFormType?.form_type_id === formType.form_type_id && (
                            <i className="fas fa-check-circle text-primary fa-lg"></i>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Inspector Selection */}
          <Col md={4} className="mb-3">
            <Card className="shadow h-100">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="fas fa-user-tie me-2"></i>
                  Select Inspector
                </h5>
              </Card.Header>
              <Card.Body style={{ position: 'relative' }}>
                {/* Disabled Overlay */}
                {!selectedFormType && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'not-allowed'
                    }}
                  >
                    <div className="text-center">
                      <i className="fas fa-lock text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                      <p className="text-muted fw-bold mb-0">Select Form Type first</p>
                    </div>
                  </div>
                )}

                {loadingInspectors ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="warning" />
                    <p className="mt-3 text-muted">Loading inspectors...</p>
                  </div>
                ) : inspectors.length === 0 && selectedFormType ? (
                  <Alert variant="warning">
                    No authorized inspectors found for this form type.
                  </Alert>
                ) : (
                  <div className="d-grid gap-3">
                    {inspectors.map((inspector) => (
                      <Card
                        key={inspector.inspector_id}
                        className={`cursor-pointer ${selectedInspector?.inspector_id === inspector.inspector_id
                            ? 'border-warning border-3'
                            : ''
                          }`}
                        onClick={() => selectedFormType && handleInspectorSelect(inspector)}
                        style={{
                          cursor: selectedFormType ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s',
                          opacity: selectedFormType ? 1 : 0.6
                        }}
                      >
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className={`me-3 text-center ${selectedInspector?.inspector_id === inspector.inspector_id
                                ? 'text-warning'
                                : 'text-secondary'
                              }`}
                            style={{ width: '40px' }}
                          >
                            <i className="fas fa-user-check fa-2x"></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 fw-bold">
                              {inspector.inspector_name}
                            </h6>
                            <small className="text-muted">
                              Code: {inspector.inspector_code}
                            </small>
                          </div>
                          {selectedInspector?.inspector_id === inspector.inspector_id && (
                            <i className="fas fa-check-circle text-warning fa-lg"></i>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Equipment Type Selection */}
          <Col md={4} className="mb-3">
            <Card className="shadow h-100">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-cogs me-2"></i>
                  Select Equipment Type
                </h5>
              </Card.Header>
              <Card.Body style={{ position: 'relative' }}>
                {/* Disabled Overlay */}
                {!selectedInspector && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'not-allowed'
                    }}
                  >
                    <div className="text-center">
                      <i className="fas fa-lock text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                      <p className="text-muted fw-bold mb-0">Select Inspector first</p>
                    </div>
                  </div>
                )}

                {loadingEquipment ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="success" />
                    <p className="mt-3 text-muted">Loading equipment types...</p>
                  </div>
                ) : equipmentTypes.length === 0 && selectedInspector ? (
                  <Alert variant="warning">
                    No equipment types available for this form type.
                  </Alert>
                ) : (
                  <>
                    {showUnauthorizedWarning && (
                      <Alert variant="warning" className="mb-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>No Authorizations Found:</strong> {selectedInspector?.full_name} doesn't have authorization to inspect any equipment for this form type.
                      </Alert>
                    )}
                    <Select
                      options={equipmentTypes.map((eq, index) => ({
                        value: eq.equipment_type_id,
                        label: getCleanEquipmentName(eq.equipment_name),
                        equipmentType: eq,
                        index: index,
                        isAuthorized: isEquipmentAuthorized(eq.equipment_type_id),
                        isDisabled: !isEquipmentAuthorized(eq.equipment_type_id)
                      }))}
                      value={
                        selectedEquipmentType
                          ? {
                            value: selectedEquipmentType.equipment_type_id,
                            label: getCleanEquipmentName(selectedEquipmentType.equipment_name),
                            equipmentType: selectedEquipmentType,
                            index: equipmentTypes.findIndex(
                              eq => eq.equipment_type_id === selectedEquipmentType.equipment_type_id
                            ),
                            isAuthorized: true
                          }
                          : null
                      }
                      onChange={(option) => {
                        if (option) {
                          handleEquipmentTypeSelect(option.equipmentType);
                        } else {
                          setSelectedEquipmentType(null);
                        }
                      }}
                      filterOption={(option, inputValue) => {
                        const eq = option.data.equipmentType;
                        const search = inputValue.toLowerCase();
                        return (
                          eq.equipment_name.toLowerCase().includes(search) ||
                          (eq.equipment_code && eq.equipment_code.toLowerCase().includes(search)) ||
                          (eq.description && eq.description.toLowerCase().includes(search))
                        );
                      }}
                      isClearable
                      isSearchable
                      placeholder="Search equipment type..."
                      noOptionsMessage={() => "No equipment types found"}
                      formatOptionLabel={formatEquipmentOption}
                      styles={equipmentSelectStyles}
                      menuPortalTarget={document.body}
                      classNamePrefix="equipment-select"
                    />
                    {selectedEquipmentType && (
                      <div className="mt-3 p-2 border rounded bg-light">
                        <small className="text-muted">
                          <i className="fas fa-check-circle text-success me-1"></i>
                          Selected: <strong>{getCleanEquipmentName(selectedEquipmentType.equipment_name)}</strong>
                        </small>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Selected Combination Display */}
        {(selectedFormType || selectedInspector || selectedEquipmentType) && (
          <Row className="mb-4">
            <Col>
              <Card className="shadow border-info">
                <Card.Body>
                  <h5 className="mb-3">
                    <i className="fas fa-info-circle text-info me-2"></i>
                    Selected Combination
                  </h5>
                  <Row>
                    <Col md={4}>
                      <strong>Form Type:</strong>{' '}
                      {selectedFormType ? (
                        <span className="text-primary">{selectedFormType.type_name}</span>
                      ) : (
                        <span className="text-muted">Not selected</span>
                      )}
                    </Col>
                    <Col md={4}>
                      <strong>Inspector:</strong>{' '}
                      {selectedInspector ? (
                        <span className="text-warning">{selectedInspector.inspector_name} ({selectedInspector.inspector_code})</span>
                      ) : (
                        <span className="text-muted">Not selected</span>
                      )}
                    </Col>
                    <Col md={4}>
                      <strong>Equipment Type:</strong>{' '}
                      {selectedEquipmentType ? (
                        <span className="text-success">{selectedEquipmentType.equipment_name}</span>
                      ) : (
                        <span className="text-muted">Not selected</span>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Continue Button */}
        <Row>
          <Col className="text-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleContinue}
              disabled={!selectedFormType || !selectedInspector || !selectedEquipmentType || loadingTemplate}
              className="shadow px-5"
            >
              {loadingTemplate ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading Template...
                </>
              ) : (
                <>
                  <i className="fas fa-arrow-right me-2"></i>
                  Continue to Form
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default NewInspectionForm;
